import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createOutboundCampaign,
  getCampaignEntries,
  getOutboundCampaign,
  getOutboundCampaigns,
  importCampaignList,
  runCampaignAction,
  updateOutboundCampaign,
} from '@/services/outbound-calls-service/campaigns.service';
import {
  CampaignAction,
  CampaignEntriesParams,
  OutboundCampaign,
  OutboundCampaignPayload,
} from '@/services/outbound-calls-service/campaigns.service.types';
import { OUTBOUND_POLL_INTERVAL_MS } from '@/services/outbound-calls-service/outbound-calls.constants';
import { ReactQueryKeys } from '@/utils/react-query-keys';

const pollWhile = (running: boolean | undefined) => (running ? OUTBOUND_POLL_INTERVAL_MS : false);

/** Polled while one of them runs, so its progress and status move on the list too. */
export const useOutboundCampaigns = (companyId?: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ReactQueryKeys.OUTBOUND_CAMPAIGNS(companyId),
    queryFn: () => getOutboundCampaigns(companyId!),
    enabled: !!companyId,
    refetchInterval: (query) =>
      pollWhile(query.state.data?.campaigns.some((campaign) => campaign.status === 'running')),
  });

  return { campaigns: data?.campaigns ?? [], isLoading, error };
};

/** Polled while running: the queue moves without the user doing anything. */
export const useOutboundCampaign = (id: number) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ReactQueryKeys.OUTBOUND_CAMPAIGN(id),
    queryFn: () => getOutboundCampaign(id),
    enabled: !!id,
    refetchInterval: (query) => pollWhile(query.state.data?.status === 'running'),
  });

  return { campaign: data, isLoading, error };
};

export const useCampaignEntries = (id: number, params: CampaignEntriesParams, poll: boolean) => {
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ReactQueryKeys.OUTBOUND_CAMPAIGN_ENTRIES(id, params),
    queryFn: () => getCampaignEntries(id, params),
    enabled: !!id,
    placeholderData: keepPreviousData,
    refetchInterval: pollWhile(poll),
  });

  return {
    entries: data?.entries ?? [],
    total: data?.entries_count ?? 0,
    isLoading,
    isFetching,
    error,
  };
};

/** Everything a write to one campaign can change: itself, its list row and its entries. */
const useRefreshCampaign = () => {
  const queryClient = useQueryClient();

  return (campaign: OutboundCampaign) => {
    queryClient.setQueryData(ReactQueryKeys.OUTBOUND_CAMPAIGN(campaign.id), campaign);
    queryClient.invalidateQueries({ queryKey: ReactQueryKeys.OUTBOUND_CAMPAIGNS_ALL() });
    queryClient.invalidateQueries({
      queryKey: ReactQueryKeys.OUTBOUND_CAMPAIGN_ENTRIES(campaign.id),
    });
  };
};

export const useCreateOutboundCampaign = (companyId?: number) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (payload: OutboundCampaignPayload) => createOutboundCampaign(companyId!, payload),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.OUTBOUND_CAMPAIGNS_ALL() }),
  });

  return { createCampaign: mutateAsync, isPending };
};

export const useUpdateOutboundCampaign = () => {
  const refresh = useRefreshCampaign();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: OutboundCampaignPayload }) =>
      updateOutboundCampaign(id, payload),
    onSuccess: refresh,
  });

  return { updateCampaign: mutateAsync, isPending };
};

export const useCampaignAction = () => {
  const refresh = useRefreshCampaign();

  const { mutateAsync, isPending, variables } = useMutation({
    mutationFn: ({ id, action }: { id: number; action: CampaignAction }) =>
      runCampaignAction(id, action),
    onSuccess: refresh,
  });

  return {
    runAction: mutateAsync,
    isPending,
    pendingAction: isPending ? variables?.action : undefined,
  };
};

export const useCampaignImport = (campaignId: number) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending, reset } = useMutation({
    mutationFn: ({ file, dryRun }: { file: File; dryRun: boolean }) =>
      importCampaignList(campaignId, file, dryRun),
    onSuccess: (_, { dryRun }) => {
      if (dryRun) return;
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.OUTBOUND_CAMPAIGN(campaignId) });
      queryClient.invalidateQueries({ queryKey: ReactQueryKeys.OUTBOUND_CAMPAIGNS_ALL() });
      queryClient.invalidateQueries({
        queryKey: ReactQueryKeys.OUTBOUND_CAMPAIGN_ENTRIES(campaignId),
      });
    },
  });

  return { importList: mutateAsync, isPending, reset };
};
